import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toTitleCase } from "../utils/titleCase";
import { Popover, PopoverTrigger } from "@radix-ui/react-popover";
import { Button } from "../ui/button";
import { PopoverContent } from "../ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Controller } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { fetchExpenses } from "./getExpense";
import { deleteExpenses } from "./deleteExpense";
import { editExpense } from "./editExpense";
import { format } from "date-fns";
import { useEditState, useSelectedState } from "./expenseStates";
import { useDeleteDialog } from "../utils/isDialogOpen";

const ExpenseTable = () => {
  const { expenses } = fetchExpenses();
  const {
    control,
    handleSubmit,
    register,
    updateExpenseFn,
    errors,
    isSubmitting,
  } = editExpense();
  const { deleteExpenseFn } = deleteExpenses();
  const { selectedExpense, setSelectedExpense } = useSelectedState();
  const { isEditOpen, setIsEditOpen } = useEditState();
  const { isDeleteDialogOpen, setIsDeleteDialogOpen } = useDeleteDialog();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-Hemmers text-lg">Description</TableHead>
          <TableHead className="font-Hemmers text-lg">Date</TableHead>
          <TableHead className="font-Hemmers text-lg">Vendor</TableHead>
          <TableHead className="font-Hemmers text-lg">Payment Method</TableHead>
          <TableHead className="font-Hemmers text-lg">Price</TableHead>
          <TableHead className="font-Hemmers text-lg">More</TableHead>
        </TableRow>
      </TableHeader>{" "}
      <TableBody>
        {" "}
        {expenses?.expenses.map((expense) => (
          <TableRow key={expense.id}>
            <TableCell className="font-Hemmers text-lg text-black">
              {toTitleCase(expense.expenseDescription)}
            </TableCell>
            <TableCell className="font-Hemmers text-lg text-black">
              {format(expense.expenseDate, "PPP")}
            </TableCell>
            <TableCell className="font-Hemmers text-lg text-black">
              {toTitleCase(expense.paidTo)}
            </TableCell>
            <TableCell className="font-Hemmers text-lg text-black">
              {toTitleCase(expense.paymentMethod)}
            </TableCell>
            <TableCell className="font-Hemmers text-lg text-black">
              {"$" + expense.expenseAmount.toFixed(2)}
            </TableCell>
            <TableCell>
              <Popover>
                <PopoverTrigger asChild>
                  <Button className="bg-transparent font-Hemmers text-lg text-black hover:bg-gray-200">
                    ...
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="pointer-events-auto w-24 p-0">
                  <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => setSelectedExpense(expense)}
                        className="w-full rounded-b-none rounded-t-md bg-[#FAFAFA] text-black hover:bg-gray-200"
                      >
                        Edit
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="border-transparent bg-inherit">
                      <DialogHeader>
                        <DialogTitle className="text-black">
                          Edit Expense
                        </DialogTitle>
                        <DialogDescription>
                          Fill in to edit an expense
                        </DialogDescription>
                      </DialogHeader>
                      <form
                        onSubmit={handleSubmit(async (data) => {
                          if (selectedExpense) {
                            try {
                              await updateExpenseFn({
                                expenseId: expense.id,
                                expenseDescription: data.expenseDescription,
                                expenseAmount: data.expenseAmount,
                                paidTo: data.paidTo,
                                expenseDate: data.expenseDate,
                                paymentMethod: data.paymentMethod,
                              });
                              setIsEditOpen(false);
                            } catch (err) {
                              console.error(err);
                            }
                          }
                        })}
                      >
                        <div className="flex space-x-4">
                          <div className="flex w-[350px] flex-col">
                            <label
                              htmlFor="expenseDescription"
                              className="block text-sm font-medium text-black"
                            >
                              Expense Description
                            </label>
                            <Input
                              {...register("expenseDescription", {
                                required: "Expense description is required",
                              })}
                              type="text"
                              id="expenseDescription"
                              autoComplete="off"
                              defaultValue={selectedExpense?.expenseDescription}
                              className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                            />
                            {errors.expenseDescription && (
                              <span className="text-sm text-rose-500">
                                {errors.expenseDescription.message}
                              </span>
                            )}
                          </div>

                          <div className="flex w-24 flex-col">
                            <label
                              htmlFor="expenseAmount"
                              className="block text-sm font-medium text-black"
                            >
                              Price
                            </label>
                            <Input
                              {...register("expenseAmount", {
                                required: "Expense amount is required",
                                valueAsNumber: true,
                              })}
                              type="number"
                              id="expenseAmount"
                              autoComplete="off"
                              defaultValue={Number(
                                selectedExpense?.expenseAmount,
                              )}
                              className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                            />
                            {errors.expenseAmount && (
                              <span className="text-sm text-rose-500">
                                {errors.expenseAmount.message}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex space-x-4">
                          <div className="mt-4 w-1/2">
                            <label
                              htmlFor="paidTo"
                              className="block text-sm font-medium text-black"
                            >
                              Vendor
                            </label>
                            <Input
                              {...register("paidTo", {
                                required: "Vendor is required",
                              })}
                              type="text"
                              id="paidTo"
                              autoComplete="off"
                              defaultValue={selectedExpense?.paidTo}
                              className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                            />
                            {errors.paidTo && (
                              <span className="text-sm text-rose-500">
                                {errors.paidTo.message}
                              </span>
                            )}
                          </div>

                          <div className="mt-4 w-1/2">
                            <label
                              htmlFor="paymentMethod"
                              className="block text-sm font-medium text-black"
                            >
                              Payment Method
                            </label>
                            <Controller
                              name="paymentMethod"
                              control={control}
                              defaultValue=""
                              render={({ field }) => (
                                <Select
                                  onValueChange={field.onChange}
                                  value={field.value || ""}
                                >
                                  <SelectTrigger className="w-full border border-zinc-300 bg-white text-left text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Credit Card">
                                      Credit Card
                                    </SelectItem>
                                    <SelectItem value="Cash">Cash</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            />
                            {errors.paymentMethod && (
                              <span className="text-sm text-rose-500">
                                {errors.paymentMethod.message}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 w-full">
                          <label
                            htmlFor="expenseDate"
                            className="block text-sm font-medium text-black"
                          >
                            Expense Date
                          </label>
                          <Controller
                            name="expenseDate"
                            control={control}
                            defaultValue={undefined}
                            render={({ field }) => (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant={"outline"}
                                    className={cn(
                                      "bordeer w-full justify-start border border-input border-zinc-300 bg-white text-left font-normal text-zinc-900 shadow-sm hover:bg-gray-200 focus:border-zinc-500 focus:ring-zinc-500",
                                      !field.value && "text-muted-foreground",
                                    )}
                                  >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {field.value ? (
                                      format(field.value, "PPP")
                                    ) : (
                                      <span>Pick a date</span>
                                    )}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="pointer-events-auto w-auto p-0">
                                  <Calendar
                                    mode="single"
                                    selected={field.value}
                                    onSelect={field.onChange}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            )}
                          />
                          {errors.expenseDate && (
                            <span className="text-sm text-rose-500">
                              {errors.expenseDate.message}
                            </span>
                          )}
                        </div>

                        <div className="mt-4">
                          <Button
                            disabled={isSubmitting}
                            className="w-full bg-[#16A34A] hover:bg-[#1bc459]"
                            type="submit"
                            onClick={() => {
                              setIsEditOpen(false);
                            }}
                          >
                            Edit Expense
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>

                  <Dialog
                    open={isDeleteDialogOpen}
                    onOpenChange={setIsDeleteDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button className="w-full rounded-b-md rounded-t-none bg-[#FAFAFA] text-black hover:bg-gray-200">
                        Delete
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="border-transparent bg-inherit">
                      <DialogHeader>
                        <DialogTitle className="text-black">
                          Are you sure you want to delete?
                        </DialogTitle>
                        <DialogDescription>
                          This action can't be undone
                        </DialogDescription>
                      </DialogHeader>
                      <Button
                        className="bg-red-400 text-white hover:bg-red-500"
                        onClick={async () => {
                          try {
                            await deleteExpenseFn({
                              expenseId: expense.id,
                            });
                            setIsDeleteDialogOpen(false);
                          } catch (error) {
                            console.error("Error deleting expense:", error);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </DialogContent>
                  </Dialog>
                </PopoverContent>
              </Popover>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ExpenseTable;
